/**
 * ModalShell - accessible dialog wrapper
 *
 * Provides a focus trap, Escape-to-close, backdrop-click dismissal, and
 * focus restoration so individual modals don't each reimplement a11y.
 */

import { type ReactNode, useCallback, useEffect, useRef } from 'react';

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface ModalShellProps {
  onClose: () => void;
  children: ReactNode;
  /** Extra class appended to the content element (e.g. 'modal-pricing'). */
  contentClassName?: string;
  /** id of the element labelling the dialog. */
  labelledBy?: string;
  /** Accessible name when no visible heading is referenced. */
  ariaLabel?: string;
}

export function ModalShell({
  onClose,
  children,
  contentClassName = '',
  labelledBy,
  ariaLabel,
}: ModalShellProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previousFocus.current = document.activeElement as HTMLElement | null;
    const content = contentRef.current;
    const firstFocusable = content?.querySelector<HTMLElement>(FOCUSABLE);
    (firstFocusable ?? content)?.focus();
    return () => previousFocus.current?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const content = contentRef.current;
      if (!content) return;
      const focusables = Array.from(content.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop dismissal is a progressive enhancement; keyboard users dismiss via Escape (handled here) or the close button
    <div
      className="modal-overlay"
      role="presentation"
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={contentRef}
        className={`modal-content ${contentClassName}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={labelledBy}
        tabIndex={-1}
      >
        {children}
      </div>
    </div>
  );
}
